import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { 
  FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock, 
  FaPaperPlane, FaFacebookF, FaInstagram 
} from 'react-icons/fa';
import { Helmet } from "react-helmet-async";
import { FaXTwitter } from 'react-icons/fa6';
import './Contact.css';

import img from '../../assets/breadcrumb/Contact-us.jpg'; // Your breadcrumb image

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Phone Number Logic
    if (name === "phone") {
        const input = value.replace(/\D/g, ""); 
        if (input.length > 0 && !["6", "7", "8", "9"].includes(input[0])) {
          return; 
        }
        setFormData({ ...formData, phone: input.slice(0, 10) });
    } else {
        setFormData({ ...formData, [name]: value });
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
        const res = await fetch(`${API_BASE_URL}/api/contact/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
        });

        const data = await res.json();

        if (res.ok && data.success) {
            Swal.fire({
                title: 'Submitted!',
                text: 'Your message has been sent successfully.',
                icon: 'success',
                confirmButtonColor: '#1e3a8a'
            });
            setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        } else {
            Swal.fire('Error!', data.message || 'Something went wrong.', 'error');
        }
    } catch (err) {
        Swal.fire('Server Error!', 'Could not connect to the server.', 'error');
    } finally {
        setLoading(false);
    }
  };

  return (
    <>
    <Helmet>
      <title>Contact Us | SK College</title>
      <meta
        name="description"
        content="Contact SK College for admission inquiries, course details, scholarships, and campus information. Reach us via phone, email, or visit our campus in Jodhpur, Rajasthan."
      />
      <meta
        name="keywords"
        content="Contact SK College, SK College Jodhpur, College Admission Inquiry, SK College Phone Number, SK College Email"
      />
      <link rel="canonical" href="https://yourdomain.com/contact" />
    </Helmet>

    <div className="contact-page-wrapper">
      
      {/* --- HEADER SECTION (ORIGINAL) --- */}
      <div className="contact-page-header-container">
        <img src={img} alt="Contact SK College" className="contact-page-header-img" />
        <div className="contact-page-header-overlay">
          <h1 className="contact-page-title">Contact Us</h1>
          <div className="contact-page-breadcrumb">
            <span className="contact-bread-static">Home</span>
            <span className="contact-page-sep">/</span>
            <span className="contact-page-bread-active">Contact</span>
          </div>
        </div>
      </div>

      <div className="contact-page-container">
        
        {/* Grid: Left = Form, Right = Info */}
        <div className="contact-page-grid">
          
          {/* --- LEFT COLUMN: FORM --- */}
          <div className="contact-page-form-col">
            <h3 className="contact-page-form-title">Send a Message</h3>
            <p className="contact-page-form-desc">We would love to hear from you. Please fill out this form.</p>
            
            <form className="contact-page-form" onSubmit={handleSubmit}>
              <div className="contact-page-form-row">
                <div className="contact-page-input-group">
                  <label>Your Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Full Name" required />
                </div>
                <div className="contact-page-input-group">
                  <label>Phone Number</label>
                  <div className="contact-page-phone-input-container">
                    <span className="contact-page-country-code">+91</span>
                    <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="XXXXX XXXXX" required />
                  </div>
                </div>
              </div>

              <div className="contact-page-input-group">
                <label>Email Address</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="example@email.com" required />
              </div>

              <div className="contact-page-input-group">
                <label>Subject</label>
                <input list="subjects" name="subject" value={formData.subject} onChange={handleInputChange} placeholder="Select or Type your Subject" className="contact-page-subject-input" required />
                <datalist id="subjects">
                  <option value="Admission Inquiry" />
                  <option value="Course Details" />
                  <option value="Examination" />
                  <option value="Scholarship" />
                  <option value="Document Request" />
                  <option value="Other" />
                </datalist>
              </div>

              <div className="contact-page-input-group">
                <label>Message</label>
                <textarea rows="5" name="message" value={formData.message} onChange={handleInputChange} placeholder="How can we help you?" required></textarea>
              </div>

              <button type="submit" className="contact-page-submit-btn" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'} <FaPaperPlane />
              </button>
            </form>
          </div>

          {/* --- RIGHT COLUMN: INFO (DIV WISE CARDS) --- */}
          <div className="contact-page-info-col">
            <div className="contact-page-info-header">
                <h2 className="contact-page-info-title">Get in Touch</h2>
                <p className="contact-page-info-subtitle">
                   Reach out to SK College administration for admissions and inquiries.
                </p>
            </div>

            <div className="contact-page-info-cards-container">
                {/* Card 1: Location */}
                <div className="contact-page-info-card">
                    <div className="contact-page-card-iconbox"><FaMapMarkerAlt /></div>
                    <div className="contact-page-card-content">
                        <h5>Our Location</h5>
                        <p>SK College Campus, Mankalao <br/>Distt. Jodhpur, Rajasthan - 342305</p>
                    </div>
                </div>

                {/* Card 2: Phone */}
                <div className="contact-page-info-card">
                    <div className="contact-page-card-iconbox"><FaPhoneAlt /></div>
                    <div className="contact-page-card-content">
                        <h5>Call Us</h5>
                        <p>+91 8003566524</p>
                        <p>+91 9057626726</p>
                    </div>
                </div>

                {/* Card 3: Email */}
                <div className="contact-page-info-card">
                    <div className="contact-page-card-iconbox"><FaEnvelope /></div>
                    <div className="contact-page-card-content">
                        <h5>Email Us</h5>
                        <p>dr.manaklao@gmail.com</p>
                    </div>
                </div>

                {/* Card 4: Hours */}
                <div className="contact-page-info-card">
                    <div className="contact-page-card-iconbox"><FaClock /></div>
                    <div className="contact-page-card-content">
                        <h5>College Hours</h5>
                        <p>Mon - Sat: 9:00 AM - 4:00 PM</p>
                        <p>Sunday: Closed</p>
                    </div>
                </div>
            </div>

            <div className="contact-page-social-wrapper">
                <h6>Follow Us:</h6>
                <div className="contact-page-socials">
                    <a href="/" className="contact-page-social-link"><FaFacebookF /></a>
                    <a href="/" className="contact-page-social-link"><FaInstagram /></a>
                    <a href="/" className="contact-page-social-link"><FaXTwitter /></a>
                </div>
            </div>
          </div>

        </div>
        
        {/* Map Section */}
        <div className="contact-page-map-section">
          <iframe 
            title="SK College Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3578.366886629235!2d73.0151!3d26.2389!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39418c4e2b6a5b1d%3A0x8e5f8f8f8f8f8f8f!2sJodhpur%2C%20Rajasthan!5e0!3m2!1sen!2sin!4v1625555555555!5m2!1sen!2sin" 
            width="100%" height="450" style={{ border: 0 }} allowFullScreen="" loading="lazy">
          </iframe>
        </div>
      </div>
    </div>
    </>
  );
}

export default Contact;